/**
 * Controller for tuning shop UI with tabs
 */

import { apiClient, TuningShop, GunType, ArmorPrice, AmmoPrice, PlayerTuning, PaymentResponse } from '../api/client';
import { soundManager } from '../game/SoundManager';

export interface TuningShopCallbacks {
  onPurchase: () => void;  // Called after successful purchase to refresh game state
}

export class TuningShopController {
  private callbacks: TuningShopCallbacks;
  private overlay!: HTMLElement;
  private shopData: TuningShop | null = null;
  private playerTuning: PlayerTuning | null = null;

  constructor(callbacks: TuningShopCallbacks) {
    this.callbacks = callbacks;
    this.bindElements();
    this.setupEventListeners();
  }

  private bindElements(): void {
    this.overlay = document.getElementById('tuning-shop')!;
  }

  private setupEventListeners(): void {
    // Shop button in main menu
    document.getElementById('menu-tuning')?.addEventListener('click', () => {
      this.openShop();
    });

    // Close button
    document.getElementById('tuning-shop-close')?.addEventListener('click', () => {
      this.closeShop();
    });

    // Click outside to close
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.closeShop();
      }
    });

    // Tab switching
    const tabs = document.querySelectorAll('.tuning-tab');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = (tab as HTMLElement).dataset.tab as 'guns' | 'armor' | 'ammo' | 'boost';
        this.switchTab(tabName);
      });
    });

    // Buy FLY button
    document.getElementById('buy-fly-btn')?.addEventListener('click', () => {
      this.openBuyFlyModal();
    });
  }

  private switchTab(tabName: 'guns' | 'armor' | 'ammo' | 'boost'): void {
    // Update tab buttons
    document.querySelectorAll('.tuning-tab').forEach(tab => {
      const isActive = (tab as HTMLElement).dataset.tab === tabName;
      tab.classList.toggle('active', isActive);
    });

    // Update tab content
    document.querySelectorAll('.tuning-tab-content').forEach(content => {
      const contentId = content.id.replace('tab-', '');
      content.classList.toggle('active', contentId === tabName);
    });
  }

  async openShop(): Promise<void> {
    this.overlay.classList.remove('hidden');

    try {
      // Load shop data and player tuning in parallel
      const [shop, tuning] = await Promise.all([
        apiClient.getTuningShop(),
        apiClient.getMyTuning()
      ]);

      this.shopData = shop;
      this.playerTuning = tuning;

      this.renderShop();
    } catch (e) {
      console.error('Failed to load shop:', e);
      this.closeShop();
    }
  }

  closeShop(): void {
    this.overlay.classList.add('hidden');
  }

  private renderShop(): void {
    if (!this.shopData || !this.playerTuning) return;

    // Update balance
    document.getElementById('tuning-shop-balance')!.textContent =
      this.playerTuning.balance.toFixed(2);

    // Render all tabs
    this.renderGuns();
    this.renderArmor();
    this.renderAmmo();
    this.renderBoost();

    // Update current equipment
    this.updateCurrentEquipment();
  }

  private renderGuns(): void {
    const container = document.getElementById('guns-list')!;
    container.innerHTML = '';

    for (const gun of this.shopData!.guns) {
      const isOwned = this.playerTuning!.gun?.id === gun.id;
      const canAfford = this.playerTuning!.balance >= gun.price;
      const fireModeText = this.getFireModeText(gun.fire_mode);

      const card = document.createElement('div');
      card.className = `tuning-card ${isOwned ? 'owned' : ''}`;

      card.innerHTML = `
        <div class="tuning-card-sprite">${this.getGunEmoji(gun.fire_mode)}</div>
        <div class="tuning-card-info">
          <div class="tuning-card-name">${gun.name}</div>
          <div class="tuning-card-desc">Магазин: ${gun.magazine_size} | ${fireModeText}</div>
          <div class="tuning-card-price">${gun.price} FLY</div>
        </div>
        <button class="tuning-card-buy" ${isOwned || !canAfford ? 'disabled' : ''}>
          ${isOwned ? '' : 'Купить'}
        </button>
      `;

      if (!isOwned && canAfford) {
        card.querySelector('.tuning-card-buy')!.addEventListener('click', () => this.buyGun(gun));
      }

      container.appendChild(card);
    }
  }

  private renderArmor(): void {
    const container = document.getElementById('armor-list')!;
    container.innerHTML = '';

    for (const armor of this.shopData!.armor_prices) {
      const canAfford = this.playerTuning!.balance >= armor.price;

      const card = document.createElement('div');
      card.className = 'tuning-card';

      card.innerHTML = `
        <div class="tuning-card-sprite">🛡️</div>
        <div class="tuning-card-info">
          <div class="tuning-card-name">+${armor.hits} защиты</div>
          <div class="tuning-card-desc">Выдержит ${armor.hits} удар(а/ов)</div>
          <div class="tuning-card-price">${armor.price} FLY</div>
        </div>
        <button class="tuning-card-buy" ${!canAfford ? 'disabled' : ''}>Купить</button>
      `;

      if (canAfford) {
        card.querySelector('.tuning-card-buy')!.addEventListener('click', () => this.buyArmor(armor));
      }

      container.appendChild(card);
    }
  }

  private renderAmmo(): void {
    const container = document.getElementById('ammo-list')!;
    container.innerHTML = '';

    const hasGun = this.playerTuning!.gun !== null;

    for (const ammo of this.shopData!.ammo_prices) {
      const canAfford = this.playerTuning!.balance >= ammo.price;
      const canBuy = canAfford && hasGun;

      const card = document.createElement('div');
      card.className = 'tuning-card';

      card.innerHTML = `
        <div class="tuning-card-sprite">💥</div>
        <div class="tuning-card-info">
          <div class="tuning-card-name">+${ammo.amount} патронов</div>
          <div class="tuning-card-desc">${!hasGun ? 'Сначала купите пулемёт!' : `Пополнить боезапас`}</div>
          <div class="tuning-card-price">${ammo.price} FLY</div>
        </div>
        <button class="tuning-card-buy" ${!canBuy ? 'disabled' : ''}>Купить</button>
      `;

      if (canBuy) {
        card.querySelector('.tuning-card-buy')!.addEventListener('click', () => this.buyAmmo(ammo));
      }

      container.appendChild(card);
    }
  }

  private renderBoost(): void {
    const container = document.getElementById('boost-list')!;
    container.innerHTML = '';

    for (const boost of this.shopData!.boost_prices) {
      const canAfford = this.playerTuning!.balance >= boost.price;

      const card = document.createElement('div');
      card.className = 'tuning-card';

      card.innerHTML = `
        <div class="tuning-card-sprite">⚡</div>
        <div class="tuning-card-info">
          <div class="tuning-card-name">+${boost.amount} ускорений</div>
          <div class="tuning-card-desc">Форсаж для уклонения</div>
          <div class="tuning-card-price">${boost.price} FLY</div>
        </div>
        <button class="tuning-card-buy" ${!canAfford ? 'disabled' : ''}>Купить</button>
      `;

      if (canAfford) {
        card.querySelector('.tuning-card-buy')!.addEventListener('click', () => this.buyBoost(boost));
      }

      container.appendChild(card);
    }
  }

  private async buyBoost(boost: { amount: number; price: number }): Promise<void> {
    try {
      const result = await apiClient.buyBoost(boost.amount);
      this.playerTuning = {
        gun: result.gun,
        ammo: result.ammo,
        armor: result.armor,
        boost: result.boost,
        balance: result.new_balance
      };
      this.showPurchaseSuccess(`+${boost.amount} ускорений`);
      this.renderShop();
      this.callbacks.onPurchase();
    } catch (e: any) {
      alert(e.message || 'Ошибка покупки');
    }
  }

  private updateCurrentEquipment(): void {
    document.getElementById('current-gun')!.textContent =
      this.playerTuning!.gun?.name || '-';
    document.getElementById('current-ammo')!.textContent =
      this.playerTuning!.ammo.toString();
    document.getElementById('current-armor')!.textContent =
      this.playerTuning!.armor.toString();
    document.getElementById('current-boost')!.textContent =
      this.playerTuning!.boost.toString();
  }

  private getFireModeText(mode: string): string {
    switch (mode) {
      case 'single': return 'Одиночные';
      case 'burst_5': return 'Очередь x5';
      case 'burst_10': return 'Очередь x10';
      case 'burst_15': return 'Очередь x15';
      default: return mode;
    }
  }

  private getGunEmoji(fireMode: string): string {
    switch (fireMode) {
      case 'single': return '🔫';
      case 'burst_5': return '🔫';
      case 'burst_10': return '💣';
      case 'burst_15': return '🚀';
      default: return '🔫';
    }
  }

  private async buyGun(gun: GunType): Promise<void> {
    try {
      const result = await apiClient.buyGun(gun.id);
      this.playerTuning = {
        gun: result.gun,
        ammo: result.ammo,
        armor: result.armor,
        boost: result.boost,
        balance: result.new_balance
      };
      this.showPurchaseSuccess(gun.name);
      this.renderShop();
      this.callbacks.onPurchase();
    } catch (e: any) {
      alert(e.message || 'Ошибка покупки');
    }
  }

  private async buyArmor(armor: ArmorPrice): Promise<void> {
    try {
      const result = await apiClient.buyArmor(armor.hits);
      this.playerTuning = {
        gun: result.gun,
        ammo: result.ammo,
        armor: result.armor,
        boost: result.boost,
        balance: result.new_balance
      };
      this.showPurchaseSuccess(`+${armor.hits} защиты`);
      this.renderShop();
      this.callbacks.onPurchase();
    } catch (e: any) {
      alert(e.message || 'Ошибка покупки');
    }
  }

  private async buyAmmo(ammo: AmmoPrice): Promise<void> {
    try {
      const result = await apiClient.buyAmmo(ammo.amount);
      this.playerTuning = {
        gun: result.gun,
        ammo: result.ammo,
        armor: result.armor,
        boost: result.boost,
        balance: result.new_balance
      };
      this.showPurchaseSuccess(`+${ammo.amount} патронов`);
      this.renderShop();
      this.callbacks.onPurchase();
    } catch (e: any) {
      alert(e.message || 'Ошибка покупки');
    }
  }

  private showPurchaseSuccess(itemName: string): void {
    // Play cash register sound
    soundManager.playCashRegister();

    // Flash balance
    const balanceEl = document.getElementById('tuning-shop-balance');
    if (balanceEl) {
      balanceEl.classList.add('tuning-pulse');
      setTimeout(() => balanceEl.classList.remove('tuning-pulse'), 300);
    }

    // Show success message
    const msg = document.createElement('div');
    msg.className = 'tuning-success-msg';
    msg.textContent = `✓ ${itemName}`;
    document.body.appendChild(msg);

    // Remove after animation
    setTimeout(() => {
      msg.remove();
    }, 1000);
  }

  // ========== Buy FLY Modal ==========

  private openBuyFlyModal(): void {
    const modal = document.getElementById('buy-fly-modal');
    if (modal && !modal.classList.contains('hidden')) {
      // Already open
      return;
    }
    if (modal) {
      modal.classList.remove('hidden');
      this.renderBuyFlyContent();
      this.setupBuyFlyListeners();
    }
  }

  private closeBuyFlyModal(): void {
    const modal = document.getElementById('buy-fly-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  private renderBuyFlyContent(): void {
    const container = document.getElementById('buy-fly-content');
    if (!container) return;

    container.innerHTML = `
      <p class="buy-fly-rate">Курс: 1 руб = 10 FLY</p>

      <div class="buy-fly-options">
        <button class="buy-fly-option" data-amount="100">
          <span class="buy-fly-rub">100 ₽</span>
          <span class="buy-fly-fly">1,000 FLY</span>
        </button>
        <button class="buy-fly-option" data-amount="300">
          <span class="buy-fly-rub">300 ₽</span>
          <span class="buy-fly-fly">3,000 FLY</span>
        </button>
        <button class="buy-fly-option" data-amount="500">
          <span class="buy-fly-rub">500 ₽</span>
          <span class="buy-fly-fly">5,000 FLY</span>
        </button>
        <button class="buy-fly-option" data-amount="1000">
          <span class="buy-fly-rub">1,000 ₽</span>
          <span class="buy-fly-fly">10,000 FLY</span>
        </button>
      </div>

      <div class="buy-fly-custom">
        <input type="number" id="buy-fly-custom-amount" placeholder="Другая сумма (мин. 100 ₽)" min="100" step="10">
        <button class="buy-fly-custom-btn" id="buy-fly-custom">Купить</button>
      </div>

      <button class="secondary-btn" id="payment-history-btn" style="width: 100%; margin-top: 16px;">
        📜 История платежей
      </button>

      <p class="buy-fly-note">Оплата через TelegaPay. После оплаты баланс обновится автоматически.</p>
      <p class="buy-fly-support">Проблемы с оплатой? <a href="https://t.me/flystackSupportBot" target="_blank">Техподдержка</a></p>
    `;
  }

  private setupBuyFlyListeners(): void {
    // Close button
    const closeBtn = document.getElementById('buy-fly-close');
    const closeFn = () => this.closeBuyFlyModal();
    closeBtn?.addEventListener('click', closeFn);

    // Amount buttons
    document.querySelectorAll('.buy-fly-option').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLElement;
        const amount = parseInt(target.dataset.amount || '0');
        if (amount > 0) {
          await this.initiatePurchase(amount);
        }
      });
    });

    // Custom amount
    const customBtn = document.getElementById('buy-fly-custom');
    customBtn?.addEventListener('click', async () => {
      const input = document.getElementById('buy-fly-custom-amount') as HTMLInputElement;
      const amount = parseInt(input.value);
      if (amount >= 100) {
        await this.initiatePurchase(amount);
      } else {
        alert('Минимальная сумма 100 рублей');
      }
    });

    // Payment history button
    const historyBtn = document.getElementById('payment-history-btn');
    historyBtn?.addEventListener('click', async () => {
      await this.showPaymentHistory();
    });
  }

  private async initiatePurchase(amountRub: number): Promise<void> {
    try {
      const payment = await apiClient.createPayment(amountRub);

      if (payment.payment_url) {
        // Show payment iframe
        this.showPaymentIframe(payment);

        // Start polling for payment status
        this.pollPaymentStatus(payment.id);
      } else {
        alert('Ошибка создания платежа: ' + (payment.error_message || 'Неизвестная ошибка'));
      }
    } catch (e: any) {
      alert('Ошибка: ' + e.message);
    }
  }

  private showPaymentIframe(payment: PaymentResponse): void {
    const container = document.getElementById('buy-fly-content');
    if (!container) return;

    container.innerHTML = `
      <div class="payment-waiting">
        <h3>Платёж создан</h3>
        <div class="payment-details">
          <p>Сумма: <strong>${payment.amount_rub} ₽</strong></p>
          <p>Вы получите: <strong>${payment.amount_fly} FLY</strong></p>
        </div>
        <button id="open-payment-btn" class="primary-btn">Открыть форму оплаты</button>
        <p class="payment-status">⏳ Ожидание оплаты...</p>
        <p class="payment-note">После оплаты баланс обновится автоматически</p>
        <button id="cancel-payment-btn" class="secondary-btn">Отменить платёж</button>
      </div>
    `;

    document.getElementById('open-payment-btn')?.addEventListener('click', () => {
      window.open(payment.payment_url!, '_blank');
    });

    document.getElementById('cancel-payment-btn')?.addEventListener('click', async () => {
      await this.cancelPayment(payment.id);
    });
  }

  private async pollPaymentStatus(paymentId: number): Promise<void> {
    const maxAttempts = 60; // 5 минут (60 * 5 секунд)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const status = await apiClient.getPaymentStatus(paymentId);

        if (status.status === 'completed') {
          this.onPaymentSuccess(status);
          return;
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          this.onPaymentFailed(status);
          return;
        }

        // Update status text
        const statusEl = document.querySelector('.payment-status');
        if (statusEl) {
          statusEl.textContent = `⏳ Ожидание оплаты... (${attempts + 1}/${maxAttempts})`;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000); // Check every 5 seconds
        } else {
          this.onPaymentTimeout();
        }
      } catch (e) {
        console.error('Failed to check payment status:', e);
        setTimeout(checkStatus, 5000);
      }
    };

    checkStatus();
  }

  private onPaymentSuccess(payment: PaymentResponse): void {
    this.closeBuyFlyModal();
    this.showPurchaseSuccess(`+${payment.amount_fly} FLY`);

    // Refresh shop to show new balance
    this.openShop();
  }

  private onPaymentFailed(payment: PaymentResponse): void {
    alert('Платёж не удался: ' + (payment.error_message || 'Неизвестная ошибка'));
    this.closeBuyFlyModal();
  }

  private onPaymentTimeout(): void {
    const container = document.getElementById('buy-fly-content');
    if (container) {
      container.innerHTML = `
        <div class="payment-waiting">
          <h3>⏱️ Время ожидания истекло</h3>
          <p>Платёж может ещё обрабатываться.</p>
          <p>Проверьте историю платежей или обратитесь в поддержку.</p>
          <button id="show-history-btn" class="primary-btn">Показать историю</button>
          <p class="buy-fly-support">
            Проблемы с оплатой? <a href="https://t.me/flystackSupportBot" target="_blank">Техподдержка</a>
          </p>
          <button id="close-timeout-btn" class="secondary-btn">Закрыть</button>
        </div>
      `;

      document.getElementById('show-history-btn')?.addEventListener('click', async () => {
        await this.showPaymentHistory();
      });

      document.getElementById('close-timeout-btn')?.addEventListener('click', () => {
        this.closeBuyFlyModal();
      });
    }
  }

  private async cancelPayment(paymentId: number): Promise<void> {
    try {
      await apiClient.cancelPayment(paymentId);
      this.closeBuyFlyModal();
    } catch (e: any) {
      alert('Ошибка отмены: ' + e.message);
    }
  }

  private async showPaymentHistory(): Promise<void> {
    const container = document.getElementById('buy-fly-content');
    if (!container) return;

    container.innerHTML = `
      <div class="payment-history-loading">
        <div class="payment-spinner"></div>
        <p>Загрузка истории...</p>
      </div>
    `;

    try {
      const history = await apiClient.getPaymentHistory(10);

      const statusLabels: { [key: string]: string } = {
        'pending': '⏳ Ожидание',
        'completed': '✅ Выполнен',
        'failed': '❌ Ошибка',
        'cancelled': '🚫 Отменён'
      };

      const historyHtml = history.length > 0
        ? history.map(p => `
            <div class="payment-history-item">
              <div class="payment-history-info">
                <span class="payment-history-amount">${p.amount_fly} FLY</span>
                <span class="payment-history-status">${statusLabels[p.status] || p.status}</span>
              </div>
              <div class="payment-history-meta">
                <span>${p.amount_rub} ₽</span>
                <span>${new Date(p.created_at).toLocaleString('ru-RU')}</span>
              </div>
            </div>
          `).join('')
        : '<p style="text-align: center; color: #8899aa;">История пуста</p>';

      container.innerHTML = `
        <h3>📜 История платежей</h3>
        <div class="payment-history-list">
          ${historyHtml}
        </div>
        <button id="back-to-buy-btn" class="secondary-btn" style="width: 100%; margin-top: 16px;">
          ← Назад к покупке
        </button>
      `;

      document.getElementById('back-to-buy-btn')?.addEventListener('click', () => {
        this.renderBuyFlyContent();
        this.setupBuyFlyListeners();
      });

    } catch (e: any) {
      container.innerHTML = `
        <div class="payment-waiting">
          <h3>❌ Ошибка</h3>
          <p>${e.message}</p>
          <button id="back-to-buy-btn" class="secondary-btn">← Назад</button>
        </div>
      `;

      document.getElementById('back-to-buy-btn')?.addEventListener('click', () => {
        this.renderBuyFlyContent();
        this.setupBuyFlyListeners();
      });
    }
  }
}
